import { Reducer } from "react";

/**
 * FightSimulator 상태 관리 리듀서
 * 1인용 - 서버 통신은 AI 요청/응답만 사용
 */

// 격투 경력 인터페이스
export interface MartialArtExperience {
  id: string;
  type: string; // 종목명 (복싱, MMA, 태권도 등)
  durationMonths: number; // 수련 기간 (개월)
}

// 헬스 정보 인터페이스
export interface HealthInfo {
  hasHealthExperience: boolean;
  yearsOfTraining: number; // 년
  benchPress: number; // kg
  deadlift: number; // kg
  squat: number; // kg
}

// 유산소 운동 정보 인터페이스
export interface CardioInfo {
  hasCardioExperience: boolean;
  maxRunningDistance: number; // km
}

// Fighter 인터페이스
export interface Fighter {
  name: string;
  height: number; // cm
  weight: number; // kg
  age: number;
  gender: "male" | "female";
  martialArts: MartialArtExperience[];
  fitnessLevel: "high" | "medium" | "low";
  hasRealFightExperience: boolean;
  health: HealthInfo;
  cardio: CardioInfo;
}

// 시뮬레이션 결과 인터페이스
export interface FightResult {
  winProbabilityA: number; // 0-100 (B는 100-A, 승자는 >50이면 A, <50이면 B, =50이면 draw)
  story: string; // 싸움 스토리 (3-5문장, 역동적 묘사)
  keyFactors: string[]; // 주요 승부 요인
}

// 페이즈
export type FightPhase = "input" | "simulating" | "result";

// 전체 상태
export interface FightSimulatorState {
  phase: FightPhase;
  fighterA: Fighter;
  fighterB: Fighter;
  isLoading: boolean;
  error: string | null;
  result: FightResult | null;
}

// 기본 Fighter 생성
const createDefaultFighter = (name: string): Fighter => ({
  name,
  height: 175,
  weight: 70,
  age: 25,
  gender: "male",
  martialArts: [],
  fitnessLevel: "medium",
  hasRealFightExperience: false,
  health: {
    hasHealthExperience: false,
    yearsOfTraining: 0,
    benchPress: 0,
    deadlift: 0,
    squat: 0,
  },
  cardio: {
    hasCardioExperience: false,
    maxRunningDistance: 0,
  },
});

// 초기 상태
export const initialFightSimulatorState: FightSimulatorState = {
  phase: "input",
  fighterA: createDefaultFighter("Fighter A"),
  fighterB: createDefaultFighter("Fighter B"),
  isLoading: false,
  error: null,
  result: null,
};

// 액션 타입
export type FightSimulatorAction =
  | { type: "UPDATE_FIGHTER_A"; payload: Partial<Fighter> }
  | { type: "UPDATE_FIGHTER_B"; payload: Partial<Fighter> }
  | { type: "ADD_MARTIAL_ART_A"; payload: MartialArtExperience }
  | { type: "ADD_MARTIAL_ART_B"; payload: MartialArtExperience }
  | { type: "REMOVE_MARTIAL_ART_A"; payload: string } // id
  | { type: "REMOVE_MARTIAL_ART_B"; payload: string } // id
  | { type: "START_SIMULATION" }
  | { type: "SIMULATION_COMPLETE"; payload: FightResult }
  | { type: "SIMULATION_ERROR"; payload: string }
  | { type: "RETRY" } // 같은 입력으로 다시 시뮬레이션
  | { type: "NEW_MATCH" } // 입력 화면으로 돌아가기
  | { type: "RESET" };

// 리듀서
export const fightSimulatorReducer: Reducer<
  FightSimulatorState,
  FightSimulatorAction
> = (state, action) => {
  switch (action.type) {
    case "UPDATE_FIGHTER_A":
      return {
        ...state,
        fighterA: { ...state.fighterA, ...action.payload },
      };

    case "UPDATE_FIGHTER_B":
      return {
        ...state,
        fighterB: { ...state.fighterB, ...action.payload },
      };

    case "ADD_MARTIAL_ART_A":
      return {
        ...state,
        fighterA: {
          ...state.fighterA,
          martialArts: [...state.fighterA.martialArts, action.payload],
        },
      };

    case "ADD_MARTIAL_ART_B":
      return {
        ...state,
        fighterB: {
          ...state.fighterB,
          martialArts: [...state.fighterB.martialArts, action.payload],
        },
      };

    case "REMOVE_MARTIAL_ART_A":
      return {
        ...state,
        fighterA: {
          ...state.fighterA,
          martialArts: state.fighterA.martialArts.filter(
            (m) => m.id !== action.payload
          ),
        },
      };

    case "REMOVE_MARTIAL_ART_B":
      return {
        ...state,
        fighterB: {
          ...state.fighterB,
          martialArts: state.fighterB.martialArts.filter(
            (m) => m.id !== action.payload
          ),
        },
      };

    case "START_SIMULATION":
      return {
        ...state,
        phase: "simulating",
        isLoading: true,
        error: null,
        result: null,
      };

    case "SIMULATION_COMPLETE":
      return {
        ...state,
        phase: "result",
        isLoading: false,
        result: action.payload,
      };

    case "SIMULATION_ERROR":
      return {
        ...state,
        phase: "input",
        isLoading: false,
        error: action.payload,
      };

    case "RETRY":
      return {
        ...state,
        phase: "simulating",
        isLoading: true,
        error: null,
        result: null,
      };

    case "NEW_MATCH":
      return {
        ...initialFightSimulatorState,
      };

    case "RESET":
      return {
        ...initialFightSimulatorState,
      };

    default:
      return state;
  }
};

// 유틸리티 함수: 입력 유효성 검사
export const isValidFighter = (fighter: Fighter): boolean => {
  return (
    fighter.name.trim().length > 0 &&
    fighter.height > 0 &&
    fighter.weight > 0 &&
    fighter.age > 0
  );
};

export const canStartSimulation = (state: FightSimulatorState): boolean => {
  return (
    isValidFighter(state.fighterA) &&
    isValidFighter(state.fighterB) &&
    !state.isLoading
  );
};
